import { LeadFormData, LeadApiResponse, LeadSubmissionConfig } from '../types';

/**
 * Development logging utilities
 */
const logDebug = (context: string, data?: any) => {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    console.log(`[LeadClient] ${context}`, data ? ':' : '');
    if (data) {
      console.log(`[LeadClient] Data:`, data);
    }
  }
};

const logError = (context: string, error: unknown, additionalData?: any) => {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    console.error(`[LeadClient] ${context}:`, error);
    if (additionalData) {
      console.error(`[LeadClient] Additional data:`, additionalData);
    }
  }
};

/**
 * Default error messages
 */
const DEFAULT_ERROR_MESSAGES = {
  SUBMISSION_FAILED: 'Failed to submit lead',
  SERVER_ERROR: 'Server error. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  GENERAL_ERROR: 'An unexpected error occurred'
};

/**
 * Generic lead submission client
 * Handles API communication with retry logic and comprehensive error handling
 */
export class LeadClient {
  private endpoint: string;
  private maxRetries: number;
  private timeout: number;
  private config: LeadSubmissionConfig;

  constructor(config: LeadSubmissionConfig) {
    this.config = config;
    this.endpoint = config.endpoint;
    this.maxRetries = config.maxRetries ?? 3; // Default: 3 retries
    this.timeout = config.timeout ?? 30000; // Default: 30 seconds
  }

  /**
   * Submit a lead with comprehensive error handling and retry logic
   */
  async submit(formData: LeadFormData, retryCount = 0): Promise<LeadApiResponse> {
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
    
    try {
      // Transform request data if transformer provided
      const requestData = this.config.transformRequest 
        ? this.config.transformRequest(formData)
        : formData;

      logDebug('Submitting lead', { 
        endpoint: this.endpoint, 
        retryCount, 
        formData: { ...formData, Token: formData.Token ? '[REDACTED]' : undefined } 
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(this.endpoint, {
        method: this.config.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...this.config.headers
        },
        body: JSON.stringify(requestData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-2xx responses
      if (!response.ok) {
        const errorDetails = {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries())
        };

        logError('API response error', null, errorDetails);

        if (response.status >= 400 && response.status < 500) {
          // Client error (4xx) - don't retry
          let errorText = 'Client error';
          try {
            errorText = await response.text();
          } catch (parseError) {
            logError('Failed to parse error response', parseError);
          }
          throw new Error(`${DEFAULT_ERROR_MESSAGES.SUBMISSION_FAILED}: ${errorText}`);
        } else if (response.status >= 500) {
          // Server error (5xx) - retry if possible
          const error = new Error(DEFAULT_ERROR_MESSAGES.SERVER_ERROR);
          (error as any).retryable = true;
          throw error;
        } else {
          throw new Error(`${DEFAULT_ERROR_MESSAGES.SUBMISSION_FAILED}: HTTP ${response.status}`);
        }
      }

      // Parse response
      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        logError('Failed to parse successful response', parseError);
        throw new Error('Invalid response format from server');
      }
      
      // Validate response structure
      if (typeof responseData !== 'object' || responseData === null) {
        logError('Invalid response structure', null, responseData);
        throw new Error('Invalid response format from server');
      }

      // Transform response if transformer provided
      const finalResponse = this.config.transformResponse 
        ? this.config.transformResponse(responseData)
        : {
            success: true,
            message: responseData.message || 'Lead submitted successfully',
            id: responseData.id,
            data: responseData
          };

      logDebug('Lead submitted successfully', { 
        responseData: { ...responseData, id: responseData.id ? '[PRESENT]' : '[MISSING]' } 
      });

      // Call success callback if provided
      if (this.config.onSuccess) {
        this.config.onSuccess(finalResponse);
      }

      return finalResponse;

    } catch (error) {
      logError('Lead submission error', error, { retryCount, maxRetries: this.maxRetries });

      // Handle abort/timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout. Please try again.');
        (timeoutError as any).retryable = true;
        throw timeoutError;
      }

      // Handle network errors
      if (error instanceof TypeError && (
        error.message.includes('fetch') || 
        error.message.includes('network') ||
        error.message.includes('Failed to fetch')
      )) {
        const networkError = new Error(DEFAULT_ERROR_MESSAGES.NETWORK_ERROR);
        (networkError as any).retryable = true;
        throw networkError;
      }

      // Check if error is retryable and we haven't exceeded max retries
      const isRetryable = (error as any).retryable === true;
      if (isRetryable && retryCount < this.maxRetries) {
        logDebug(`Retrying in ${retryDelay}ms`, { retryCount: retryCount + 1 });
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.submit(formData, retryCount + 1);
      }
      
      // Call error callback if provided
      if (this.config.onError && error instanceof Error) {
        this.config.onError(error);
      }

      // Re-throw known errors
      if (error instanceof Error) {
        throw error;
      }
      
      // Handle unknown errors
      throw new Error(DEFAULT_ERROR_MESSAGES.GENERAL_ERROR);
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<LeadSubmissionConfig>) {
    this.config = { ...this.config, ...newConfig };
    if (newConfig.endpoint) {
      this.endpoint = newConfig.endpoint;
    }
  }
}